<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Avatar Component
      p.text--secondary A versatile avatar component based on Vuetify's VAvatar for displaying user profile images, initials, or icons.

    // Avatar Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in avatarVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped avatars (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="avatar in variation.avatars" :key="avatar.label" class="d-flex flex-column align-center")
                  yz-avatar(v-bind="avatar.props")
                    template(v-if="avatar.content") {{ avatar.content }}
                    template(v-else-if="avatar.icon")
                      yz-icon {{ avatar.icon }}
                  small.text--secondary.mt-2(v-if="avatar.hint") {{ avatar.hint }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="avatar in variation.avatars" :key="avatar.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === avatar.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === avatar.code ? 'success' : ''" @click="copyCode(avatar.code)")
                    span.font-weight-medium.ml-2 {{ avatar.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ avatar.code }}

          // Individual avatars
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-avatar(v-bind="variation.props")
                template(v-if="variation.content") {{ variation.content }}
                template(v-else-if="variation.icon")
                  yz-icon {{ variation.icon }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in avatarProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      avatarVariations: [
        {
          name: 'Basic Avatars',
          id: 'basic-avatars',
          description: 'Standard avatars with images, initials, and icons',
          isGroup: true,
          avatars: [
            {
              label: 'Image',
              props: {
                src: 'https://picsum.photos/100/100?random=1',
                size: '56'
              },
              code: 'yz-avatar(src="https://picsum.photos/100/100" size="56")',
              hint: 'Profile image'
            },
            {
              label: 'Initials',
              props: {
                color: 'yz-blue-500',
                size: '56'
              },
              content: 'JD',
              code: 'yz-avatar(color="yz-blue-500" size="56") JD',
              hint: 'User initials'
            },
            {
              label: 'Icon',
              props: {
                color: 'yz-neutral-400',
                size: '56'
              },
              icon: 'mdi-account',
              code: 'yz-avatar(color="yz-neutral-400" size="56"): yz-icon mdi-account',
              hint: 'Default icon'
            }
          ]
        },
        {
          name: 'Avatar Sizes',
          id: 'avatar-sizes',
          description: 'Different sizes for various use cases',
          isGroup: true,
          avatars: [
            {
              label: 'Extra Small',
              props: {
                src: 'https://picsum.photos/100/100?random=2',
                size: '24'
              },
              code: 'yz-avatar(src="..." size="24")',
              hint: '24px'
            },
            {
              label: 'Small',
              props: {
                src: 'https://picsum.photos/100/100?random=3',
                size: '32'
              },
              code: 'yz-avatar(src="..." size="32")',
              hint: '32px'
            },
            {
              label: 'Default',
              props: {
                src: 'https://picsum.photos/100/100?random=4'
              },
              code: 'yz-avatar(src="...")',
              hint: '48px'
            },
            {
              label: 'Large',
              props: {
                src: 'https://picsum.photos/100/100?random=5',
                size: '56'
              },
              code: 'yz-avatar(src="..." size="56")',
              hint: '56px'
            },
            {
              label: 'Extra Large',
              props: {
                src: 'https://picsum.photos/100/100?random=6',
                size: '72'
              },
              code: 'yz-avatar(src="..." size="72")',
              hint: '72px'
            },
            {
              label: 'Custom',
              props: {
                src: 'https://picsum.photos/100/100?random=7',
                size: '96'
              },
              code: 'yz-avatar(src="..." size="96")',
              hint: '96px'
            }
          ]
        },
        {
          name: 'Color Variations',
          id: 'color-variations',
          description: 'Avatars with different background colors from YZ palette',
          isGroup: true,
          avatars: [
            {
              label: 'Success',
              props: {
                color: 'yz-success',
                size: '56'
              },
              content: 'AB',
              code: 'yz-avatar(color="yz-success" size="56") AB'
            },
            {
              label: 'Warning',
              props: {
                color: 'yz-warning',
                size: '56'
              },
              content: 'CD',
              code: 'yz-avatar(color="yz-warning" size="56") CD'
            },
            {
              label: 'Danger',
              props: {
                color: 'yz-danger',
                size: '56'
              },
              content: 'EF',
              code: 'yz-avatar(color="yz-danger" size="56") EF'
            },
            {
              label: 'Blue',
              props: {
                color: 'yz-blue-500',
                size: '56'
              },
              content: 'GH',
              code: 'yz-avatar(color="yz-blue-500" size="56") GH'
            },
            {
              label: 'Green',
              props: {
                color: 'yz-green-500',
                size: '56'
              },
              content: 'IJ',
              code: 'yz-avatar(color="yz-green-500" size="56") IJ'
            },
            {
              label: 'Neutral',
              props: {
                color: 'yz-neutral-600',
                size: '56'
              },
              content: 'KL',
              code: 'yz-avatar(color="yz-neutral-600" size="56") KL'
            }
          ]
        },
        {
          name: 'Icon Avatars',
          id: 'icon-avatars',
          description: 'Avatars featuring different icons with colors',
          isGroup: true,
          avatars: [
            {
              label: 'User',
              props: {
                color: 'yz-blue-500',
                size: '56'
              },
              icon: 'mdi-account',
              code: 'yz-avatar(color="yz-blue-500" size="56"): yz-icon mdi-account'
            },
            {
              label: 'Admin',
              props: {
                color: 'yz-warning-500',
                size: '56'
              },
              icon: 'mdi-shield-account',
              code: 'yz-avatar(color="yz-warning-500" size="56"): yz-icon mdi-shield-account'
            },
            {
              label: 'Support',
              props: {
                color: 'yz-success-500',
                size: '56'
              },
              icon: 'mdi-headset',
              code: 'yz-avatar(color="yz-success-500" size="56"): yz-icon mdi-headset'
            },
            {
              label: 'Settings',
              props: {
                color: 'yz-neutral-500',
                size: '56'
              },
              icon: 'mdi-cog',
              code: 'yz-avatar(color="yz-neutral-500" size="56"): yz-icon mdi-cog'
            }
          ]
        },
        {
          name: 'Tile Avatars',
          id: 'tile-avatars',
          description: 'Square avatars using tile prop',
          isGroup: true,
          avatars: [
            {
              label: 'Tile Image',
              props: {
                src: 'https://picsum.photos/100/100?random=8',
                size: '56',
                tile: true
              },
              code: 'yz-avatar(src="..." size="56" tile)',
              hint: 'Square image'
            },
            {
              label: 'Tile Initials',
              props: {
                color: 'yz-blue-500',
                size: '56',
                tile: true
              },
              content: 'MN',
              code: 'yz-avatar(color="yz-blue-500" size="56" tile) MN',
              hint: 'Square initials'
            },
            {
              label: 'Tile Icon',
              props: {
                color: 'yz-green-500',
                size: '56',
                tile: true
              },
              icon: 'mdi-folder',
              code: 'yz-avatar(color="yz-green-500" size="56" tile): yz-icon mdi-folder',
              hint: 'Square icon'
            }
          ]
        },
        {
          name: 'Avatar Groups',
          id: 'avatar-groups',
          description: 'Multiple avatars in different arrangements',
          isGroup: true,
          avatars: [
            {
              label: 'Overlapping',
              props: {
                src: 'https://picsum.photos/100/100?random=9',
                size: '40',
                class: 'mr-n2'
              },
              code: 'yz-avatar.mr-n2(src="..." size="40")',
              hint: 'Negative margin'
            },
            {
              label: 'Second',
              props: {
                src: 'https://picsum.photos/100/100?random=10',
                size: '40',
                class: 'mr-n2'
              },
              code: 'yz-avatar.mr-n2(src="..." size="40")',
              hint: 'Overlapped'
            },
            {
              label: 'Third',
              props: {
                src: 'https://picsum.photos/100/100?random=11',
                size: '40'
              },
              code: 'yz-avatar(src="..." size="40")',
              hint: 'Last in group'
            }
          ]
        },
        {
          name: 'Status Indicators',
          id: 'status-indicators',
          description: 'Avatars with online/offline status indicators',
          isGroup: true,
          avatars: [
            {
              label: 'Online',
              props: {
                src: 'https://picsum.photos/100/100?random=12',
                size: '56',
                class: 'position-relative'
              },
              code: 'yz-avatar.position-relative(src="..." size="56")\n  // Add status badge',
              hint: 'With status'
            },
            {
              label: 'Offline',
              props: {
                src: 'https://picsum.photos/100/100?random=13',
                size: '56',
                class: 'position-relative'
              },
              code: 'yz-avatar.position-relative(src="..." size="56")\n  // Add offline badge',
              hint: 'Offline status'
            }
          ]
        }
      ],
      avatarProps: [
        {
          name: 'src',
          type: 'String',
          default: 'undefined',
          description: 'Image source URL for the avatar'
        },
        {
          name: 'alt',
          type: 'String',
          default: 'undefined',
          description: 'Alt text for the avatar image'
        },
        {
          name: 'color',
          type: 'String',
          default: 'undefined',
          description: 'Background color using YZ color palette (e.g., yz-success, yz-blue-500)'
        },
        {
          name: 'size',
          type: 'Number | String',
          default: '48',
          description: 'Size of the avatar in pixels'
        },
        {
          name: 'tile',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the avatar square instead of circular'
        },
        {
          name: 'left',
          type: 'Boolean',
          default: 'false',
          description: 'Applies left margin for spacing'
        },
        {
          name: 'right',
          type: 'Boolean',
          default: 'false',
          description: 'Applies right margin for spacing'
        },
        {
          name: 'height',
          type: 'Number | String',
          default: 'undefined',
          description: 'Height of the avatar (overrides size)'
        },
        {
          name: 'width',
          type: 'Number | String',
          default: 'undefined',
          description: 'Width of the avatar (overrides size)'
        },
        {
          name: 'maxHeight',
          type: 'Number | String',
          default: 'undefined',
          description: 'Maximum height constraint'
        },
        {
          name: 'maxWidth',
          type: 'Number | String',
          default: 'undefined',
          description: 'Maximum width constraint'
        },
        {
          name: 'minHeight',
          type: 'Number | String',
          default: 'undefined',
          description: 'Minimum height constraint'
        },
        {
          name: 'minWidth',
          type: 'Number | String',
          default: 'undefined',
          description: 'Minimum width constraint'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-4 > *:not(:last-child) {
  margin-right: 16px;
}
</style>